import React, { useMemo, useState, useRef, useEffect, useCallback, createContext, useContext } from 'react';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

var noop = function noop() {
  return undefined;
};

var initialResource = [undefined, {
  error: undefined,
  loading: true,
  reload: noop,
  abort: noop
}];

function transformData(resource, transform) {
  var data = resource[0],
      extra = resource[1];
  return [transform(data), extra];
}

function useTransform(resource, transform) {
  var data = resource[0],
      extra = resource[1];
  var newData = useMemo(function () {
    return transform(data);
  }, [data, transform]);
  return [newData, extra];
}
function useLocalState(resource) {
  var _useState = useState(resource[0]),
      value = _useState[0],
      setValue = _useState[1];

  var dataRef = useRef();
  var data = resource[0],
      extra = resource[1];

  if (dataRef.current !== data) {
    dataRef.current = data;
    setValue(data);
  }

  return [value, _extends({}, extra, {
    setData: setValue
  })];
}
function useDefault(resource, defaultData) {
  var ref = useRef(defaultData);
  return transformData(resource, function (data) {
    return data != null ? data : ref.current;
  });
}
function useSmoothReload(resource) {
  var data = resource[0],
      extra = resource[1];
  var dataRef = useRef(data);
  useEffect(function () {
    if (data !== undefined) {
      dataRef.current = data;
    }
  }, [data]);
  return extra.loading ? [dataRef.current, extra] : resource;
}
function createResourceHook(request) {
  return function (requestArgs, _temp) {
    var _ref = _temp === void 0 ? {} : _temp,
        _ref$deps = _ref.deps,
        deps = _ref$deps === void 0 ? [] : _ref$deps,
        _ref$condition = _ref.condition,
        condition = _ref$condition === void 0 ? true : _ref$condition;

    var _useState2 = useState(true),
        reloadFlag = _useState2[0],
        setReloadFlag = _useState2[1];

    var reload = useCallback(function () {
      return setReloadFlag(function (preFlag) {
        return !preFlag;
      });
    }, []);

    var _useState3 = useState({}),
        _useState3$ = _useState3[0],
        promise = _useState3$.promise,
        _useState3$$abort = _useState3$.abort,
        abort = _useState3$$abort === void 0 ? noop : _useState3$$abort,
        setRequestResult = _useState3[1];

    var _useState4 = useState([undefined, undefined, condition]),
        _useState4$ = _useState4[0],
        data = _useState4$[0],
        error = _useState4$[1],
        loading = _useState4$[2],
        setRequestState = _useState4[1];

    useEffect(function () {
      if (!condition) {
        return setRequestResult({});
      }

      var result = request.apply(void 0, requestArgs);
      var requestResult = 'abort' in result ? result : {
        promise: result,
        abort: noop
      }; // The rejected promise will always be handled later (in the next useEffect)
      // handle it immediately to prevent unhandled Promise rejection warning

      requestResult.promise["catch"](noop);
      setRequestResult(requestResult);
      return requestResult.abort; // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [].concat(deps, [condition, reloadFlag]));
    useEffect(function () {
      if (!promise) setRequestState([undefined, undefined, condition]);
    }, [condition, promise]);
    useEffect(function () {
      if (!promise) return;
      var isCurrent = true;
      setRequestState([undefined, undefined, true]);
      promise.then(function (d) {
        return isCurrent && setRequestState([d, undefined, false]);
      })["catch"](function (e) {
        return isCurrent && setRequestState([undefined, e, false]);
      });
      return function () {
        isCurrent = false;
      };
    }, [promise]);
    return [data, {
      error: error,
      loading: loading,
      reload: reload,
      abort: abort
    }];
  };
}
function createResourceStore(resourceDescriptor, initialValue, useTriggerEffect) {
  var Context = createContext(initialValue);

  var useResource = function useResource() {
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    useTriggerEffect == null ? void 0 : useTriggerEffect.apply(void 0, args);
    return useContext(Context);
  };

  var Provider = function Provider(_ref2) {
    var children = _ref2.children,
        restProps = _objectWithoutPropertiesLoose(_ref2, ["children"]);

    var providerProps = restProps;
    var value = resourceDescriptor(providerProps);
    return React.createElement(Context.Provider, {
      value: value
    }, children);
  };

  return {
    useResource: useResource,
    Provider: Provider
  };
}
var createLazyResourceHelper = function createLazyResourceHelper() {
  var requested = false;

  var setRequested = function setRequested() {};

  var reset = function reset() {
    requested = false;
    setRequested(false);
  };

  var useTriggerEffect = function useTriggerEffect(triggered) {
    if (triggered === void 0) {
      triggered = true;
    }

    return useEffect(function () {
      if (triggered) {
        requested = true;
        setRequested(true);
      }
    }, [triggered]);
  };

  var useTriggered = function useTriggered() {
    var _useState5 = useState(requested),
        triggered = _useState5[0],
        setTriggered = _useState5[1];

    setRequested = setTriggered;
    return triggered;
  };

  return {
    useTriggerEffect: useTriggerEffect,
    reset: reset,
    useTriggered: useTriggered
  };
};
function createLazyResourceStore(lazyResourceDescriptor, initialValue) {
  var _createLazyResourceHe = createLazyResourceHelper(),
      useTriggerEffect = _createLazyResourceHe.useTriggerEffect,
      reset = _createLazyResourceHe.reset,
      useTriggered = _createLazyResourceHe.useTriggered;

  return _extends({}, createResourceStore(lazyResourceDescriptor({
    useTriggered: useTriggered
  }), initialValue, useTriggerEffect), {
    reset: reset
  });
}

export { createLazyResourceHelper, createLazyResourceStore, createResourceHook, createResourceStore, initialResource, useDefault, useLocalState, useSmoothReload, useTransform };
//# sourceMappingURL=use-resource.esm.js.map
